import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2, Radio, Globe2, ArrowLeft, Users, Download } from "lucide-react";
import Crest from "../components/Crest";
import { NATIONS, REGIONS, TIERS } from "../data/nations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TAGS = ["Proclamation", "Council", "Trade", "Travel", "Parliament", "Treaty", "Milestone", "Kingdom"];
const COUNCIL_REGIONS = REGIONS.filter((r) => r !== "All");

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full rounded-lg bg-[#0D1013] border border-[#00A651]/20 px-4 py-3 text-sm text-[#F7F5F0] placeholder:text-[#8A99AD]/60 focus:outline-none focus:border-[#00A651]/60 transition-colors";
const labelCls = "font-official text-[10px] tracking-[0.25em] uppercase text-[#8A99AD] block mb-2";

function Login({ onLogin }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      localStorage.setItem("usa_admin_token", data.access_token);
      onLogin(data.access_token);
      toast.success("Welcome to the Leadership Desk.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 noise-overlay relative" data-testid="admin-login-view">
      <div className="absolute inset-0 gold-glow opacity-50" />
      <form
        onSubmit={submit}
        className="relative w-full max-w-md rounded-2xl border border-[#00A651]/25 bg-[#121519] p-8 sm:p-10"
        data-testid="admin-login-form"
      >
        <div className="flex items-center gap-3">
          <Crest className="w-10 h-10" />
          <div>
            <p className="font-official text-[10px] tracking-[0.3em] uppercase text-[#00A651]">Restricted</p>
            <h1 className="font-display font-extrabold text-xl text-[#F7F5F0]">Leadership Desk</h1>
          </div>
        </div>
        <div className="mt-8 space-y-5">
          <div>
            <label className={labelCls}>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="secretariat@usafrica.movement"
              className={inputCls}
              data-testid="admin-email-input"
            />
          </div>
          <div>
            <label className={labelCls}>Password</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder="••••••••••••"
              className={inputCls}
              data-testid="admin-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#00A651] text-[#0A0C0E] font-display font-bold text-sm px-7 py-4 hover:bg-[#2FC36A] disabled:opacity-60 transition-all duration-300"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Enter The Desk
          </button>
        </div>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 font-official text-[10px] tracking-[0.2em] uppercase text-[#8A99AD] hover:text-[#00A651] transition-colors" data-testid="admin-back-home-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to the Union
        </Link>
      </form>
    </div>
  );
}

function DispatchPanel({ authHeaders, onAuthError }) {
  const [form, setForm] = useState({ scope: "union", region: "West", country_code: "KK", tag: "Proclamation", title: "", excerpt: "" });
  const [items, setItems] = useState(null);
  const [publishing, setPublishing] = useState(false);

  const load = () =>
    axios.get(`${API}/dispatches/all`, authHeaders()).then((r) => setItems(r.data)).catch(onAuthError);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const publish = async (e) => {
    e.preventDefault();
    setPublishing(true);
    try {
      const payload = {
        scope: form.scope,
        tag: form.tag,
        title: form.title,
        excerpt: form.excerpt,
        region: form.scope === "council" ? form.region : null,
        country_code: form.scope === "country" ? form.country_code : null,
      };
      await axios.post(`${API}/dispatches`, payload, authHeaders());
      toast.success("Dispatch published to the Union.");
      setForm((f) => ({ ...f, title: "", excerpt: "" }));
      load();
    } catch (err) {
      if (err.response?.status === 401) onAuthError();
      else toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPublishing(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/dispatches/${id}`, authHeaders());
      toast.success("Dispatch withdrawn.");
      setItems((list) => list.filter((d) => d.id !== id));
    } catch (err) {
      if (err.response?.status === 401) onAuthError();
      else toast.error("Could not withdraw the dispatch.");
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-10" data-testid="dispatch-panel">
      <form onSubmit={publish} className="rounded-2xl border border-[#00A651]/20 bg-[#121519] p-6 sm:p-8 space-y-5 h-fit" data-testid="dispatch-publish-form">
        <p className="font-official text-[10px] tracking-[0.3em] uppercase text-[#00A651]">Publish a Dispatch</p>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className={labelCls}>Scope</label>
            <select value={form.scope} onChange={(e) => setForm((f) => ({ ...f, scope: e.target.value }))} className={inputCls} data-testid="dispatch-scope-select">
              <option value="union">Union-Wide</option>
              <option value="council">Regional Council</option>
              <option value="country">Single Nation</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Tag</label>
            <select value={form.tag} onChange={(e) => setForm((f) => ({ ...f, tag: e.target.value }))} className={inputCls} data-testid="dispatch-tag-select">
              {TAGS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>
        {form.scope === "council" && (
          <div>
            <label className={labelCls}>Council Region</label>
            <select value={form.region} onChange={(e) => setForm((f) => ({ ...f, region: e.target.value }))} className={inputCls} data-testid="dispatch-region-select">
              {COUNCIL_REGIONS.map((r) => (
                <option key={r} value={r}>{r} Africa</option>
              ))}
            </select>
          </div>
        )}
        {form.scope === "country" && (
          <div>
            <label className={labelCls}>Nation</label>
            <select value={form.country_code} onChange={(e) => setForm((f) => ({ ...f, country_code: e.target.value }))} className={inputCls} data-testid="dispatch-country-select">
              {NATIONS.map((n) => (
                <option key={n.code} value={n.code}>{n.name}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className={labelCls}>Headline</label>
          <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="The official announcement" className={inputCls} data-testid="dispatch-title-input" />
        </div>
        <div>
          <label className={labelCls}>Dispatch Text</label>
          <textarea value={form.excerpt} onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))} rows={4} placeholder="Two or three sentences, in the voice of the Union." className={inputCls} data-testid="dispatch-excerpt-input" />
        </div>
        <button
          type="submit"
          disabled={publishing || !form.title.trim() || !form.excerpt.trim()}
          data-testid="dispatch-publish-btn"
          className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#00A651] text-[#0A0C0E] font-display font-bold text-sm px-7 py-4 hover:bg-[#2FC36A] disabled:opacity-60 transition-all duration-300"
        >
          {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />}
          Publish To The Union
        </button>
      </form>

      <div>
        <p className="font-official text-[10px] tracking-[0.3em] uppercase text-[#00A651] mb-5">Published Dispatches</p>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1" data-testid="dispatch-list">
          {items === null && <div className="animate-pulse rounded-xl border border-[#00A651]/10 bg-[#121519] h-24" />}
          {items !== null &&
            items.map((d) => (
              <div key={d.id} className="rounded-xl border border-[#00A651]/12 bg-[#121519] p-4 flex gap-4 items-start" data-testid={`dispatch-row-${d.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-official text-[9px] tracking-[0.25em] uppercase text-[#00A651]">{d.tag}</span>
                    <span className="font-official text-[9px] tracking-[0.15em] uppercase text-[#8A99AD]">
                      {d.scope === "union" ? "Union-Wide" : d.scope === "council" ? `${d.region} Council` : d.country_code}
                      {" • "}
                      {new Date(d.published_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </span>
                  </div>
                  <h4 className="font-display font-bold text-sm text-[#F7F5F0] mt-1.5 leading-snug">{d.title}</h4>
                </div>
                <button
                  onClick={() => remove(d.id)}
                  data-testid={`dispatch-delete-${d.id}`}
                  className="shrink-0 w-8 h-8 rounded-full border border-[#EF3340]/40 text-[#EF3340] flex items-center justify-center hover:bg-[#EF3340]/10 transition-colors"
                  title="Withdraw dispatch"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function VisaPanel({ authHeaders, onAuthError }) {
  const [overrides, setOverrides] = useState({});
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    axios.get(`${API}/nations/visa-status`).then((r) => setOverrides(r.data.overrides || {})).catch(() => {});
  }, []);

  const nations = useMemo(
    () =>
      NATIONS.map((n) => (overrides[n.code] !== undefined ? { ...n, visaFree: overrides[n.code] } : n)).filter((n) =>
        n.name.toLowerCase().includes(query.trim().toLowerCase())
      ),
    [overrides, query]
  );

  const toggle = async (n) => {
    setBusy(n.code);
    try {
      const next = !n.visaFree;
      await axios.post(`${API}/admin/visa-status`, { code: n.code, visa_free: next }, authHeaders());
      setOverrides((o) => ({ ...o, [n.code]: next }));
      toast.success(`${n.name} is now ${next ? "Visa-Free" : "Visa on Arrival"}.`);
    } catch (err) {
      if (err.response?.status === 401) onAuthError();
      else toast.error("Could not update the visa status.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="visa-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <p className="font-official text-[10px] tracking-[0.3em] uppercase text-[#00A651]">
          Visa Status — {NATIONS.filter((n) => (overrides[n.code] !== undefined ? overrides[n.code] : n.visaFree)).length} Visa-Free / 55
        </p>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a nation..."
          className={`${inputCls} sm:max-w-xs rounded-full`}
          data-testid="visa-search-input"
        />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {nations.map((n) => (
          <div key={n.code} className="rounded-xl border border-[#00A651]/12 bg-[#121519] p-4 flex items-center justify-between gap-3" data-testid={`visa-row-${n.code.toLowerCase()}`}>
            <div className="min-w-0">
              <p className={`font-display font-bold text-sm truncate ${n.visaFree ? "text-[#4ADE80]" : "text-[#EF3340]"}`}>{n.name}</p>
              <p className="font-official text-[9px] tracking-[0.2em] uppercase text-[#8A99AD] mt-1">
                {n.visaFree ? "Visa-Free" : "Visa on Arrival"}
              </p>
            </div>
            <button
              onClick={() => toggle(n)}
              disabled={busy === n.code}
              data-testid={`visa-toggle-${n.code.toLowerCase()}`}
              className={`shrink-0 rounded-full px-4 py-2 font-official text-[9px] tracking-[0.15em] uppercase border transition-all duration-300 ${
                n.visaFree
                  ? "border-[#EF3340]/50 text-[#EF3340] hover:bg-[#EF3340]/10"
                  : "border-[#4ADE80]/50 text-[#4ADE80] hover:bg-[#4ADE80]/10"
              }`}
            >
              {busy === n.code ? "..." : n.visaFree ? "Set On Arrival" : "Set Visa-Free"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function RegistryPanel({ authHeaders, onAuthError }) {
  const [members, setMembers] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    axios.get(`${API}/admin/members`, authHeaders()).then((r) => setMembers(r.data)).catch(onAuthError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tierLabel = (v) => TIERS.find((t) => t.value === v)?.label || v;

  const filtered = useMemo(() => {
    if (!members) return [];
    const q = query.trim().toLowerCase();
    return members.filter(
      (m) =>
        m.full_name.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q) ||
        m.country.toLowerCase().includes(q) ||
        m.movement_id.toLowerCase().includes(q)
    );
  }, [members, query]);

  const exportCsv = () => {
    if (!members || members.length === 0) return;
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = members.map((m) =>
      [m.full_name, m.email, m.country, tierLabel(m.role), m.movement_id, m.created_at].map(esc).join(",")
    );
    const csv = ["Full Name,Email,Country,Station,Movement ID,Joined", ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "usa-movement-registry.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Registry exported.");
  };

  return (
    <div data-testid="registry-panel">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <p className="font-official text-[10px] tracking-[0.3em] uppercase text-[#00A651]" data-testid="registry-count">
          {members === null ? "Loading the registry..." : `${members.length} Charter Signers`}
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, nation, ID..."
            className={`${inputCls} sm:max-w-xs rounded-full`}
            data-testid="registry-search-input"
          />
          <button
            onClick={exportCsv}
            disabled={!members || members.length === 0}
            data-testid="registry-export-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-[#00A651]/40 text-[#00A651] font-display font-bold text-xs px-5 py-3 hover:bg-[#00A651]/10 disabled:opacity-50 transition-all duration-300"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>
      <div className="space-y-3" data-testid="registry-list">
        {members === null && <div className="animate-pulse rounded-xl border border-[#00A651]/10 bg-[#121519] h-24" />}
        {members !== null && filtered.length === 0 && (
          <p className="font-official text-[10px] tracking-[0.2em] uppercase text-[#8A99AD]" data-testid="registry-empty">
            No signers match.
          </p>
        )}
        {filtered.map((m, i) => (
          <div key={m.id} className="rounded-xl border border-[#00A651]/12 bg-[#121519] p-4 grid sm:grid-cols-12 gap-2 sm:items-center" data-testid={`registry-row-${i}`}>
            <div className="sm:col-span-3 min-w-0">
              <p className="font-display font-bold text-sm text-[#F7F5F0] truncate">{m.full_name}</p>
              <p className="font-official text-[10px] text-[#8A99AD] truncate mt-0.5">{m.email}</p>
            </div>
            <p className="sm:col-span-3 font-official text-[10px] tracking-[0.15em] uppercase text-[#8A99AD]">{m.country}</p>
            <p className="sm:col-span-2 font-official text-[10px] tracking-[0.15em] uppercase text-[#8A99AD]">{tierLabel(m.role)}</p>
            <p className="sm:col-span-2 font-official text-[11px] tracking-[0.15em] text-[#00A651]">{m.movement_id}</p>
            <p className="sm:col-span-2 font-official text-[10px] tracking-[0.15em] uppercase text-[#8A99AD] sm:text-right">
              {new Date(m.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDesk() {
  const [token, setToken] = useState(() => localStorage.getItem("usa_admin_token"));
  const [tab, setTab] = useState("dispatches");

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });
  const logout = () => {
    localStorage.removeItem("usa_admin_token");
    setToken(null);
  };
  const onAuthError = () => {
    toast.error("Session expired. Enter the desk again.");
    logout();
  };

  if (!token) return <Login onLogin={setToken} />;

  return (
    <div className="min-h-screen noise-overlay relative" data-testid="admin-desk">
      <div className="relative px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#00A651]/15 pb-6">
          <div className="flex items-center gap-3">
            <Crest className="w-10 h-10" />
            <div>
              <p className="font-official text-[10px] tracking-[0.3em] uppercase text-[#00A651]">United States of Africa</p>
              <h1 className="font-display font-extrabold text-xl text-[#F7F5F0]">Leadership Desk</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="font-official text-[10px] tracking-[0.2em] uppercase text-[#8A99AD] hover:text-[#00A651] transition-colors" data-testid="desk-back-home-link">
              View Site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[#00A651]/30 text-[#00A651] font-display font-bold text-xs px-5 py-2.5 hover:bg-[#00A651]/10 transition-all duration-300"
            >
              <LogOut className="w-3.5 h-3.5" /> Leave The Desk
            </button>
          </div>
        </div>

        <div className="flex gap-2 mt-8 mb-10">
          <button
            onClick={() => setTab("dispatches")}
            data-testid="tab-dispatches"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-official text-[11px] tracking-[0.15em] uppercase transition-all duration-300 ${
              tab === "dispatches" ? "bg-[#00A651] text-[#0A0C0E]" : "border border-[#00A651]/20 text-[#8A99AD] hover:text-[#00A651]"
            }`}
          >
            <Radio className="w-3.5 h-3.5" /> Dispatches
          </button>
          <button
            onClick={() => setTab("visa")}
            data-testid="tab-visa"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-official text-[11px] tracking-[0.15em] uppercase transition-all duration-300 ${
              tab === "visa" ? "bg-[#00A651] text-[#0A0C0E]" : "border border-[#00A651]/20 text-[#8A99AD] hover:text-[#00A651]"
            }`}
          >
            <Globe2 className="w-3.5 h-3.5" /> Visa Status
          </button>
          <button
            onClick={() => setTab("registry")}
            data-testid="tab-registry"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-official text-[11px] tracking-[0.15em] uppercase transition-all duration-300 ${
              tab === "registry" ? "bg-[#00A651] text-[#0A0C0E]" : "border border-[#00A651]/20 text-[#8A99AD] hover:text-[#00A651]"
            }`}
          >
            <Users className="w-3.5 h-3.5" /> Registry
          </button>
        </div>

        {tab === "dispatches" ? (
          <DispatchPanel authHeaders={authHeaders} onAuthError={onAuthError} />
        ) : tab === "visa" ? (
          <VisaPanel authHeaders={authHeaders} onAuthError={onAuthError} />
        ) : (
          <RegistryPanel authHeaders={authHeaders} onAuthError={onAuthError} />
        )}
      </div>
    </div>
  );
}
